// ── Veezi preview toggle (LOCAL DEV ONLY) ────────────────────────────────────
// Activates the Veezi-sourced preview for a venue (currently only "anthology").
// Runtime-based so it works on an ALREADY-RUNNING dev server without a restart:
//   http://localhost:3000/?veeziPreview=anthology   (then hard-refresh)
// The choice sticks in sessionStorage so it survives in-app navigation; load
// with ?veeziPreview=  (empty) to turn it back off. Falls back to the
// REACT_APP_VEEZI_PREVIEW build-time env var. Prod never sets either, so this
// resolves to null and every preview code path is inert.
export const VEEZI_PREVIEW = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('veeziPreview')) {
      const v = params.get('veeziPreview') || '';
      if (v) sessionStorage.setItem('veeziPreview', v);
      else sessionStorage.removeItem('veeziPreview');
      return v || null;
    }
    const stored = sessionStorage.getItem('veeziPreview');
    if (stored) return stored;
  } catch (_) { /* SSR / no window — ignore */ }
  return process.env.REACT_APP_VEEZI_PREVIEW || null;
})();

export const THEATERS = [
  { id: "symphony-space", name: "Leonard Nimoy Thalia at Symphony Space", slug: "symphony-space" },
  { id: "60853", name: "Alamo Drafthouse Downtown Brooklyn", slug: "alamo-drafthouse-downtown-brooklyn" },
  { id: "67766", name: "Alamo Drafthouse Lower Manhattan", slug: "alamo-drafthouse-lower-manhattan" },
  { id: "67768", name: "Alamo Drafthouse Staten Island", slug: "alamo-drafthouse-staten-island" },
  { id: "56432", name: "Angelika Film Center & Café", slug: "angelika-film-center-cafe" },
  // Anthology: crawler-owned (crawl-anthology.js, HTML-primary + Veezi-validated,
  // no IST). Re-enabled 2026-07-06 now that the dedicated crawler bypasses IST's
  // fuzzy-matching. sync-showtimes skips 41518; cleanup-duplicates owns it.
  { id: "41518", name: "Anthology Film Archives", slug: "anthology" },
  { id: "65749", name: "Brooklyn Academy of Music Rose Cinemas (BAM)", slug: "bam-rose-cinemas" },
  { id: "60749", name: "Cinema 123 by Angelika", slug: "cinema-123-by-angelika" },
  { id: "41515", name: "Film Forum", slug: "film-forum" },
  { id: "66390", name: "Film at Lincoln Center - Walter Reade Theater", slug: "film-at-lincoln-center-walter-reade" },
  { id: "41517", name: "IFC Center", slug: "ifc" },
  { id: "81344", name: "Low Cinema", slug: "low-cinema" },
  { id: "65751", name: "Metrograph", slug: "metrograph" },
  // AMC + Regal return 2026-07-27 via the Fathom crawler (crawl-fathom.js) —
  // same 20 venues that left in d05e636, same ids/slugs.
  { id: "41527", name: "AMC 34th Street 14", slug: "amc-34th-street-14" },
  { id: "41531", name: "AMC Village 7", slug: "amc-village-7" },
  { id: "41533", name: "AMC 19th St. East 6", slug: "amc-19th-st-east-6" },
  { id: "41523", name: "Regal Union Square", slug: "regal-union-square" },
  { id: "41514", name: "AMC Empire 25", slug: "amc-empire-25" },
  { id: "41529", name: "AMC Magic Johnson Harlem 9", slug: "amc-magic-johnson-harlem-9" },
  { id: "41530", name: "AMC Kips Bay 15", slug: "amc-kips-bay-15" },
  { id: "41532", name: "AMC Lincoln Square 13", slug: "amc-lincoln-square-13" },
  { id: "41542", name: "AMC Orpheum 7", slug: "amc-orpheum-7" },
  { id: "41544", name: "AMC 84th Street 6", slug: "amc-84th-street-6" },
  { id: "41534", name: "Regal Battery Park", slug: "regal-battery-park" },
  { id: "56487", name: "Regal Times Square", slug: "regal-times-square" },
  { id: "45552", name: "AMC Bay Plaza Cinema 13", slug: "amc-bay-plaza-13" },
  { id: "45544", name: "Regal Sheepshead Bay", slug: "regal-sheepshead-bay" },
  { id: "45546", name: "AMC Fresh Meadows 7", slug: "amc-fresh-meadows-7" },
  { id: "45550", name: "AMC Bay Terrace 6", slug: "amc-bay-terrace-6" },
  { id: "45551", name: "Regal Atlas Park", slug: "regal-atlas-park" },
  { id: "45554", name: "Regal UA Midway", slug: "regal-ua-midway" },
  { id: "48863", name: "Regal Kaufman Astoria", slug: "regal-kaufman-astoria" },
  { id: "65057", name: "AMC DINE-IN Staten Island 11", slug: "amc-staten-island-11" },
  { id: "66783", name: "Museum of Modern Art (MoMA)", slug: "moma" },
  { id: "65959", name: "Museum of the Moving Image", slug: "museum-of-the-moving-image" },
  { id: "65753", name: "Nitehawk Williamsburg", slug: "nitehawk-williamsburg" },
  { id: "65752", name: "Nitehawk Prospect Park", slug: "nitehawk-prospect-park" },
  { id: "60750", name: "Paris Theater", slug: "paris-theatre-new-york" },
  { id: "65750", name: "Quad Cinema", slug: "quad-cinema" },
  { id: "65747", name: "Roxy", slug: "roxy" },
  { id: "81345", name: "Spectacle", slug: "spectacle" }, // SHIPPED 2026-07-28 (crawl-spectacle-background — site + WP feed + pools.events)
  { id: "81346", name: "Film Noir Cinema", slug: "film-noir-cinema" }, // SHIPPED 2026-07-28 (crawl-film-noir-background — Squarespace eventlist)
  { id: "81347", name: "New Plaza Cinema", slug: "new-plaza-cinema" }, // SHIPPED 2026-07-28 (crawl-new-plaza — Veezi ticketing, weekend venue @ MAD)
  { id: "81348", name: "Cobble Hill Cinemas", slug: "cobble-hill-cinemas" }, // SHIPPED 2026-08-12 (crawl-cobble-hill-background — hk-cinemas platform; Curated Classics + Silent Clowns land here)
  { id: "81349", name: "Asia Society", slug: "asia-society" }, // SHIPPED 2026-08-10 (crawl-asia-society-background — Firecrawl; Museums filter group)
  { id: "81350", name: "Light Industry", slug: "light-industry" }, // SHIPPED 2026-08-12 (crawl-light-industry — plain fetch, director-corroborated matching)
  { id: "81351", name: "Uptown Film Center", slug: "uptown-film-center" }, // SHIPPED 2026-08-19 (crawl-uptown-film — Events Calendar REST API; roving nonprofit reclaiming the Metro Theater)
  { id: "81352", name: "Williamsburg Cinemas", slug: "williamsburg-cinemas" }, // SHIPPED 2026-08-28 (crawl-cobble-hill-background multi-venue — H&K location 00004)
  { id: "81353", name: "Kew Gardens Cinemas", slug: "kew-gardens-cinemas" }, // SHIPPED 2026-08-28 (crawl-cobble-hill-background multi-venue — H&K location 00002)
  { id: "65900", name: "Syndicated", slug: "syndicated" },
  { id: "41535", name: "Village East by Angelika", slug: "village-east-by-angelika" },
  // AMC ×13 + Regal ×7 — TURNED OFF 2026-07-16 (Josh's call): the IST chains were the
  // source of nearly all recurring errors (new films mislabeled onto old records; opera/
  // kids-series/first-run junk re-dumped every 4h) for a modest event-cinema rep slice.
  // Removed from the UI here + THEATER_CHAINS below; IST sync + cleanup-chains crons
  // disabled in netlify.toml; rows deleted. REVERSIBLE — re-add these + the AMC/Regal
  // groups in THEATER_CHAINS + the crons to turn them back on.
  // Outdoor (seasonal — populated by sync-outdoor-screenings.js, not IST)
  { id: "outdoor-rcc", name: "Rooftop Cinema Club", slug: "rooftop-cinema-club" },
  { id: "outdoor-nyc-parks", name: "NYC Parks", slug: "nyc-parks-movies-under-the-stars" },
  { id: "outdoor-bbp", name: "Brooklyn Bridge Park", slug: "brooklyn-bridge-park-movies-with-a-view" },
  { id: "outdoor-intrepid", name: "Intrepid Museum", slug: "intrepid-museum-movie-nights" },
  { id: "outdoor-lic", name: "LIC Waterfront", slug: "lic-waterfront-movies" },
  { id: "outdoor-bryant", name: "Paramount+ Movie Night at Bryant Park", slug: "bryant-park-movie-nights" },
]
  .sort((a, b) => a.name.localeCompare(b.name));

// Outdoor virtual theater IDs — these don't come from IST and only appear when
// the seasonal "Outdoor Movies" toggle has data. Code that needs to distinguish
// real vs. virtual theaters can check membership here.
export const OUTDOOR_THEATER_IDS = ["outdoor-rcc", "outdoor-nyc-parks", "outdoor-bbp", "outdoor-intrepid", "outdoor-lic", "outdoor-bryant"];

export const THEATER_LOCATIONS = {
  // Brooklyn
  "60853": "Brooklyn", // Alamo Drafthouse Downtown Brooklyn
  "65749": "Brooklyn", // BAM Rose Cinemas
  "65752": "Brooklyn", // Nitehawk Prospect Park
  "65753": "Brooklyn", // Nitehawk Williamsburg
  "65900": "Brooklyn", // Syndicated
  
  // Keep the existing theater IDs and add their locations
  "67766": "Lower Manhattan", // Alamo Drafthouse Lower Manhattan
  "67768": "Staten Island", // Alamo Drafthouse Staten Island
  "56432": "Lower Manhattan", // Angelika Film Center & Café
  "41518": "Lower Manhattan", // Anthology
  "60749": "Upper East Side", // Cinema 123 by Angelika (60th & 3rd)
  "41515": "Lower Manhattan", // Film Forum
  "66390": "Upper West Side", // Film at Lincoln Center (Walter Reade)
  "41517": "Lower Manhattan", // IFC
  "81344": "Queens", // Low Cinema
  "65751": "Lower Manhattan", // Metrograph
  "66783": "Midtown Manhattan", // Museum of Modern Art (MoMA)
  "65959": "Queens", // Museum of the Moving Image
  "60750": "Midtown Manhattan", // Paris Theatre
  "65750": "Lower Manhattan", // Quad Cinema
  "65747": "Lower Manhattan", // Roxy
  "81345": "Brooklyn", // Spectacle
  "81346": "Brooklyn", // Film Noir Cinema (Greenpoint)
  "81347": "Midtown Manhattan", // New Plaza Cinema @ MAD
  "81348": "Brooklyn", // Cobble Hill Cinemas
  "81349": "Upper East Side", // Asia Society (725 Park Ave at 70th St)
  "81350": "Brooklyn", // Light Industry (361 Stagg St, East Williamsburg)
  "81351": "Upper West Side", // Uptown Film Center (roving: Amsterdam Ave, Riverside Park, JCC Manhattan)
  "81352": "Brooklyn", // Williamsburg Cinemas
  "symphony-space": "Upper West Side",
  "81353": "Queens", // Kew Gardens Cinemas
  "41535": "Lower Manhattan", // Village East by Angelika
  "41527": "Midtown Manhattan", // AMC 34th Street 14
  "41531": "Lower Manhattan", // AMC Village 7
  "41533": "Midtown Manhattan", // AMC 19th St. East 6
  "41523": "Lower Manhattan", // Regal Union Square
  // Additional AMC Manhattan
  "41514": "Midtown Manhattan", // AMC Empire 25 (42nd St)
  "41529": "Harlem", // AMC Magic Johnson Harlem 9
  "41530": "Midtown Manhattan", // AMC Kips Bay 15
  "41532": "Upper West Side", // AMC Lincoln Square 13 (68th St)
  "41542": "Upper East Side", // AMC Orpheum 7 (86th St & 3rd Ave)
  "41544": "Upper East Side", // AMC 84th Street 6 (84th & 1st)
  // Additional Regal Manhattan
  "41534": "Lower Manhattan", // Regal Battery Park
  "56487": "Midtown Manhattan", // Regal Times Square (E-Walk)
  // Bronx
  "45552": "The Bronx", // AMC Bay Plaza Cinema 13
  // Brooklyn
  "45544": "Brooklyn", // Regal Sheepshead Bay
  // Queens
  "45546": "Queens", // AMC Fresh Meadows 7
  "45550": "Queens", // AMC Bay Terrace 6 (Bayside)
  "45551": "Queens", // Regal Atlas Park (Glendale)
  "45554": "Queens", // Regal UA Midway (Forest Hills)
  "48863": "Queens", // Regal Kaufman Astoria
  // Staten Island
  "65057": "Staten Island", // AMC DINE-IN Staten Island 11
  // Outdoor virtual theaters — RCC is one fixed Midtown rooftop; Rooftop Films
  // anchors in Brooklyn (Industry City + Sunset Park). NYC Parks plays in
  // different parks every night so it has no single bucket — the per-screening
  // outdoor_venue field carries the actual park + borough.
  "outdoor-rcc": "Midtown Manhattan",
  "outdoor-bbp": "Brooklyn",
  "outdoor-intrepid": "Midtown Manhattan",
  "outdoor-lic": "Queens",
  "outdoor-bryant": "Midtown Manhattan",
};

export const LOCATIONS = [
  "The Bronx",
  "Brooklyn",
  "Queens",
  "Staten Island",
  "Lower Manhattan",
  "Midtown Manhattan",
  "Upper East Side",
  "Upper West Side",
  "Harlem"
];

// Theater website URLs (homepage, not ticket link)
export const THEATER_WEBSITES = {
  "60853": "https://drafthouse.com/nyc/theater/downtown-brooklyn",
  "67766": "https://drafthouse.com/nyc/theater/lower-manhattan",
  "67768": "https://drafthouse.com/nyc/theater/staten-island",
  "56432": "https://www.angelikafilmcenter.com/nyc",
  "41518": "https://anthologyfilmarchives.org",
  "65749": "https://www.bam.org/cinema",
  "60749": "https://www.angelikafilmcenter.com/cinema123",
  "41515": "https://filmforum.org",
  "66390": "https://www.filmlinc.org",
  "41517": "https://www.ifccenter.com",
  "81344": "https://lowcinema.com",
  "65751": "https://metrograph.com",
  "66783": "https://www.moma.org/calendar/film",
  "65959": "https://www.movingimage.us",
  "65753": "https://nitehawkcinema.com/williamsburg",
  "65752": "https://nitehawkcinema.com/prospectpark",
  "60750": "https://www.paristheaternyc.com",
  "65750": "https://quadcinema.com",
  "65747": "https://www.roxycinemanewyork.com/",
  "81345": "https://www.spectacletheater.com/",
  "81346": "https://www.filmnoircinema.com/",
  "81347": "https://newplazacinema.org/",
  "81348": "https://www.hk-cinemas.com/movie-theatres/brooklyn/new-york/cobble-hill-cinemas",
  "81352": "https://www.hk-cinemas.com/movie-theatres/brooklyn/new-york/williamsburg-cinemas",
  "symphony-space": "https://www.symphonyspace.org/",
  "81353": "https://www.hk-cinemas.com/movie-theatres/queens/new-york/kew-gardens-cinemas",
  "81349": "https://asiasociety.org/new-york/events",
  "81350": "https://www.lightindustry.org/calendar/",
  "81351": "https://uptownfilm.org/calendar/month/",
  "65900": "https://syndicatedbk.com",
  "41535": "https://www.angelikafilmcenter.com/villageeast",
  // Outdoor virtual theaters
  "outdoor-rcc": "https://rooftopcinemaclub.com/us/new-york/midtown",
  "outdoor-nyc-parks": "https://www.nycgovparks.org/events/free_summer_movies",
  "outdoor-bbp": "https://brooklynbridgepark.org/event-series/movies-with-a-view/",
  "outdoor-intrepid": "https://intrepidmuseum.org/",
  "outdoor-lic": "https://www.hunterspointparks.org/",
  "outdoor-bryant": "https://bryantpark.org/activities/movie-nights",
};

// Street addresses — entries marked ⚠️ need verification
export const THEATER_ADDRESSES = {
  "81349": "725 Park Ave, New York, NY 10021", // Asia Society (at E 70th St)
  "81350": "361 Stagg St, Suite 407, Brooklyn, NY 11206", // Light Industry
  "81351": "2626 Broadway at W 99th St, New York, NY 10025", // Uptown Film Center (the landmarked Metro Theater — screenings roam nearby until it reopens)
  // Alamo Drafthouse
  "60853": "445 Albee Square W, Brooklyn, NY 11201",
  "67766": "28 Liberty St, New York, NY 10005",
  "67768": "2636 Hylan Blvd Unit 34, Staten Island, NY 10306",
  // Angelika
  "56432": "18 W Houston St, New York, NY 10012",
  "60749": "1001 3rd Ave, New York, NY 10022",
  "41535": "181 2nd Ave, New York, NY 10003",
  // Independent
  "41518": "32 Second Ave, New York, NY 10003",
  "65749": "30 Lafayette Ave, Brooklyn, NY 11217",
  "41515": "209 W Houston St, New York, NY 10014",
  "66390": "165 W 65th St, New York, NY 10023",
  "41517": "323 Sixth Ave, New York, NY 10014",
  "81344": "70-11 60th St, Ridgewood, NY 11385",
  "65751": "7 Ludlow St, New York, NY 10002",
  "66783": "11 W 53rd St, New York, NY 10019",
  "65959": "36-01 35th Ave, Astoria, NY 11106",
  "65753": "136 Metropolitan Ave, Brooklyn, NY 11249",
  "65752": "188 Prospect Park West, Brooklyn, NY 11215",
  "60750": "4 W 58th St, New York, NY 10019",
  "65750": "34 W 13th St, New York, NY 10011",
  "65747": "2 6th Ave, New York, NY 10013",
  "81345": "124 South 3rd St, Brooklyn, NY 11249",
  "81346": "122 Meserole Ave, Brooklyn, NY 11222",
  "81347": "2 Columbus Circle, New York, NY 10019",
  "81348": "265 Court Street, Brooklyn, NY 11231",
  "81352": "217 Grand Street, Brooklyn, NY 11211", // Williamsburg Cinemas
  "symphony-space": "250 West 95th Street, New York, NY 10025",
  "81353": "81-05 Lefferts Boulevard, Kew Gardens, NY 11415", // Kew Gardens Cinemas
  "65900": "40 Bogart St, Brooklyn, NY 11206",
  // AMC Manhattan
  "41527": "312 W 34th St, New York, NY 10001",
  "41531": "66 3rd Ave, New York, NY 10003",
  "41533": "890 Broadway, New York, NY 10003",
  "41514": "234 W 42nd St, New York, NY 10036",
  "41529": "2309 Frederick Douglass Blvd, New York, NY 10027",
  "41530": "570 2nd Ave, New York, NY 10016",
  "41532": "1998 Broadway, New York, NY 10023",
  "41542": "1538 3rd Ave, New York, NY 10028",
  "41544": "2310 Broadway, New York, NY 10024",
  // AMC Boroughs
  "45552": "2100 Bartow Ave, Bronx, NY 10475",
  "45546": "18602 Union Tpke, Fresh Meadows, NY 11366",
  "45550": "23-21 Bell Blvd, Bayside, NY 11360",
  "65057": "2655 Richmond Ave, Staten Island, NY 10314",
  // Regal
  "41523": "850 Broadway, New York, NY 10003",
  "41534": "102 North End Ave, New York, NY 10282",
  "56487": "247 W 42nd St, New York, NY 10036",
  "45544": "3907 Emmons Ave, Brooklyn, NY 11235",
  "45551": "80-29 Cooper Ave, Glendale, NY 11385",
  "45554": "108-22 Queens Blvd, Forest Hills, NY 11375",
  "48863": "35-30 38th St, Astoria, NY 11101",
  // Outdoor virtual theaters
  "outdoor-rcc": "230 5th Ave, New York, NY 10001",
  "outdoor-nyc-parks": "Various NYC Parks (all five boroughs)",
  "outdoor-bbp": "Brooklyn Bridge Park, Pier 1 Harbor View Lawn, Brooklyn, NY",
  "outdoor-intrepid": "Intrepid Museum, Pier 86, W 46th St & 12th Ave, New York, NY",
  "outdoor-lic": "Hunter's Point South Park, Center Blvd, Long Island City, NY 11101",
  "outdoor-bryant": "Bryant Park, New York, NY 10018",
};

// About text for revival/arthouse theaters — chain multiplexes omitted
export const THEATER_ABOUT = {
  "81352": "Williamsburg Cinemas is H&K's seven-screen Grand Street multiplex. Alongside first-run programming, it hosts the family's Curated Classics repertory series, bringing big-screen revivals to Williamsburg.",
  "symphony-space": "Symphony Space’s Leonard Nimoy Thalia hosts repertory film programs, including the Silent Clowns Film Series with live piano accompaniment.",
  "81353": "Kew Gardens Cinemas is H&K's landmark Austin Street-adjacent arthouse in Queens, a neighborhood fixture since 1935. Its repertory slice comes through the family's Curated Classics series.",
  "81350": "Light Industry is a Brooklyn venue for film and electronic art founded in 2008, presenting weekly screenings of archival rarities, avant-garde work, and under-seen classics, frequently on 16mm and often with writers and artists presenting in person.",
  "81351": "The Uptown Film Center is the nonprofit campaign to reclaim the landmarked 1933 Metro Theater on Broadway at 99th Street as a five-screen arthouse — the only full-time repertory cinema north of 72nd Street. Until the marquee relights, they program roving screenings around the neighborhood: outdoors on Amsterdam Avenue, in Riverside Park, and indoors at the JCC.",
  "81349": "Asia Society's film program at its Park Avenue headquarters presents rare and classic Asian cinema in curated series, frequently on 35mm archival prints in partnership with institutions like the National Film Archive of Japan and Anthology Film Archives.",
  "41518": "Essential archive for experimental and avant-garde cinema. Founded by Jonas Mekas, it's the home of underground film in NYC — two screens, low frills, maximum cinephilia.",
  "65749": "Brooklyn's premiere repertory venue. BAM programs adventurous double features, retrospectives, and revival series alongside new arthouse releases, all under one of NYC's great performing arts institutions.",
  "41515": "New York's gold standard for repertory cinema. Two screens dedicated to new restorations, director retrospectives, and classic double features. If a film is worth reviving, Film Forum has probably shown it.",
  "66390": "Home of the New York Film Festival and year-round international and repertory programming. The Walter Reade Theater is one of New York's great screening rooms.",
  "41517": "West Village anchor for midnight movies, director Q&As, and smart repertory programming. Beloved for its cult horror series and commitment to genuinely weird cinema.",
  "81344": "Queens arthouse with a focus on overlooked and experimental cinema. Small, programmer-led, and worth the trip.",
  "65751": "Lower East Side arthouse with impeccable taste. Known for 35mm projection, Criterion-worthy programming, and a café-bar that makes it worth arriving early.",
  "66783": "The Museum of Modern Art screens film as art — world cinema, preservation projects, and retrospectives tied to the collection. One of the world's great film archives.",
  "65959": "Astoria's essential film and media institution. Specializes in genre cinema, animation, and international film with a permanent collection of movie artifacts you can explore before the show.",
  "65753": "Brooklyn's dine-in arthouse. Full bar and food service during screenings, with programming that mixes new releases with smart revival picks. Great for films you want to treat as an event.",
  "65752": "The quieter Nitehawk. Smaller, more intimate, same quality programming as Williamsburg. Park Slope's answer to a proper neighborhood cinema.",
  "60750": "One of New York's last single-screen theaters, restored to its original glamour. A 58th Street landmark that survived by sheer force of history.",
  "65750": "West Village four-screen staple for foreign and revival cinema. One of the most dedicated revival programmers in NYC — if something is playing in 35mm, Quad probably has it.",
  "65747": "TriBeCa newcomer with ambitions to rival Film Forum. Focused on 35mm and 70mm screenings of classics, with a programming philosophy that puts projection quality first.",
  "81345": "Volunteer-run microcinema in Williamsburg (est. 2010) showing overlooked, obscure, and radical cinema seven days a week — retrospectives, avant-garde programs, and midnight oddities. Cash-only at the door.",
  "81346": "Greenpoint microcinema devoted to noir, cult, and genre repertory — plus surprise-screening nights like Cult Cinema and Film Club. Steps from the Greenpoint Ave G stop.",
  "81347": "The Lincoln Plaza successor: a nonprofit arthouse now screening weekends at the Museum of Arts and Design on Columbus Circle — first-run arthouse plus the Big Screen Classics repertory program.",
  "65900": "Bushwick's bar-cinema hybrid. Drinks and movies in a loft space, with themed screenings and late-night programming.",
  "41535": "Landmarked East Village theater with an ornate interior. Worth visiting for restoration screenings and the building itself.",
  "56432": "SoHo institution for arthouse and independent film. The subway rumble is part of the experience. Six screens, strong programming.",
  "60749": "Upper East Side Angelika outpost with a mix of art films and quality mainstream programming.",
  "60853": "NYC outpost of the beloved Austin-based chain. Full service dining, strict no-phone policy, and programming that skews cinephile with regular marathons and revival series.",
  "67766": "Downtown Manhattan Alamo with the same dine-in format and no-tolerance phone policy, plus regular themed events and classic series.",
  "67768": "Staten Island's answer to a proper dine-in cinema. Same Alamo format — food, strict phone policy, and intentional programming.",
  "outdoor-rcc": "Rooftop dine-in cinema at 230 5th Ave in Midtown. Programs current releases, classics, sing-alongs, and themed series under the open sky. Open seasonally (spring through fall).",
  "outdoor-nyc-parks": "Free outdoor movies at parks across all five boroughs, programmed by the NYC Department of Parks & Recreation and the Mayor's Office of Media and Entertainment. Family-friendly programming runs from late May through August.",
  "outdoor-bbp": "Free Thursday-night outdoor screenings on Pier 1's Harbor View Lawn in Brooklyn Bridge Park, with the Manhattan skyline as a backdrop. The \"Movies With A View\" series runs July through September.",
  "outdoor-intrepid": "Free outdoor movie nights on Pier 86 at the Intrepid Sea, Air & Space Museum on the Hudson River. A handful of crowd-pleasers each summer programmed alongside the museum's flight deck.",
  "outdoor-lic": "Free summer movies on the oval at Hunter's Point South Park, on the Long Island City waterfront with the Manhattan skyline across the river. Presented by Hunters Point Parks Conservancy; films start at sunset.",
  "outdoor-bryant": "Bryant Park Movie Nights: a beloved NYC summer tradition of free Monday-night classics on the lawn behind the New York Public Library. The lawn opens at 5pm; films begin at 8pm, mid-July through mid-September.",
};

// Official org info for each chain — used in JSON-LD parentOrganization
export const THEATER_CHAIN_ORGS = {
  "Alamo Drafthouse": { name: "Alamo Drafthouse Cinema", url: "https://drafthouse.com" },
  "AMC":              { name: "AMC Theatres",            url: "https://www.amctheatres.com" },
  "Angelika":         { name: "Angelika Film Center",    url: "https://www.angelikafilmcenter.com" },
  "Nitehawk":         { name: "Nitehawk Cinema",         url: "https://nitehawkcinema.com" },
  "Regal":            { name: "Regal Cinemas",           url: "https://www.regmovies.com" },
};

// Chain groupings for the theater filter dropdown
export const THEATER_CHAINS = [
  {
    // Museums group (Josh 2026-08-10, at the Asia Society QA): the museum
    // film programs read as their own category, not "Independent Cinemas".
    name: "Museums",
    ids: ["66783", "65959", "81349"] // MoMA, MoMI, Asia Society
  },
  {
    // H&K Cinemas group (Josh 2026-08-28, at the Williamsburg/Kew QA): the
    // three-house family screens the same Curated Classics slate.
    name: "H&K Cinemas",
    ids: ["81348", "81352", "81353"] // Cobble Hill, Williamsburg, Kew Gardens
  },
  {
    name: "Alamo Drafthouse",
    ids: ["60853", "67766", "67768"]
  },
  // AMC + Regal groups removed 2026-07-16 (chains turned off — see THEATERS above).
  {
    name: "AMC",
    ids: ["41514", "41527", "41529", "41530", "41531", "41532", "41533", "41542", "41544", "45546", "45550", "45552", "65057"]
  },
  {
    name: "Regal",
    ids: ["41523", "41534", "56487", "45544", "45551", "45554", "48863"]
  },
  {
    name: "Angelika",
    ids: ["56432", "60749", "41535"]
  },
  {
    name: "Nitehawk",
    ids: ["65752", "65753"]
  },
];

// List of theater IDs that return titles in all caps
export const UPPERCASE_THEATER_IDS = [
  "56432",  // Angelika Film Center
  "41535",  // Village East by Angelika
  "41518",  // Anthology
  "41515",   // Film Forum
  "60749"   // Cinema 123 by Angelika
];

// Theaters with custom ticket links (when API doesn't provide booking_link)
export const CUSTOM_TICKET_LINKS = {
  "60853": "https://drafthouse.com/nyc/theater/downtown-brooklyn", // Alamo Drafthouse Downtown Brooklyn
  "67766": "https://drafthouse.com/nyc/theater/lower-manhattan", // Alamo Drafthouse Lower Manhattan
  "67768": "https://drafthouse.com/nyc/theater/staten-island", // Alamo Drafthouse Staten Island
  "81344": "https://lowcinema.com/tickets", // Low Cinema
  "65959": "https://movingimage.org/events/list/?tribe_filterbar_category_custom%5B0%5D=253&tribe_filterbar_category_custom%5B1%5D=230", // Museum of the Moving Image
  "65750": "https://quadcinema.com/", // Quad Cinema
  // MoMA: 2026-06-06 — fall back to the generic film calendar landing page
  // when a row has no specific event URL. Previously this was null (no
  // button), but ~149 MoMA rows lacked a per-event URL and showed plain
  // text "No booking link" with no recourse. The specific event URLs we DO
  // have (via lib/bookingLinkOverrides.js for foreign-titled rep
  // programming) still win because getTicketLink prefers a non-null
  // bookingLink over this fallback. So users see the specific MoMA event
  // page when we have it, and the general film calendar otherwise.
  "66783": "https://www.moma.org/calendar/film",
};

// Theater IDs that always use the CUSTOM_TICKET_LINKS even when the IST API
// returns a booking_link. Quad's IST-supplied URLs go to Fandango landing
// pages that don't deep-link to the specific showtime; user prefers the
// venue's own site.
const FORCE_CUSTOM_LINK_THEATER_IDS = new Set(['65750']);

// Returns a working ticket link: fixes broken Alamo URLs, forces custom-link
// theaters to their own site, falls back to custom links when none provided.
export function getTicketLink(bookingLink, theaterId) {
  if (FORCE_CUSTOM_LINK_THEATER_IDS.has(theaterId)) {
    return CUSTOM_TICKET_LINKS[theaterId] || null;
  }
  if (bookingLink && bookingLink.includes('drafthouse.com/undefined/')) {
    return bookingLink.replace('/undefined/', '/nyc/');
  }
  return bookingLink || CUSTOM_TICKET_LINKS[theaterId] || null;
}

export const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const BATCH_SIZE = 25;


// export const DEBUG = false; // Set to false by default
// export const MONTHS_TO_FETCH = 3; // Reduce from 12 to 3 months
// export const INITIAL_DAYS_TO_SHOW = 30;